let loggingIn = false;
let loginInterval = null;
let resumedPasswordChange = false;
const sessionIdKey = 'x-ss-sessionId';
import * as Auth from '@/modules/authv2';
import { api } from '@/modules/apiv2';

export default {
  state: {
    currentUser: null,
    loggedInStatus: 'pending',
    passwordChangeState: localStorage.getItem('x-ss-passwordChanged'),
  },

  getters: {
    currentUser(state) {
      return state.currentUser;
    },

    currentUserName(state) {
      return state.currentUser?.idTokenClaims.name;
    },

    currentUserClaims(state) {
      return state.currentUser?.idTokenClaims;
    },

    currentUserOrgType(state) {
      const orgType = state.currentUser?.idTokenClaims.extension_OrgType.toLowerCase();

      if (orgType && orgType.includes('owner')) {
        return 'owner';
      } else if (orgType && orgType.includes('consumer')) {
        return 'consumer';
      }

      return undefined;
    },

    currentUserAccountType(state) {
      return state.currentUser?.idTokenClaims.extension_AccountType;
    },

    currentUserOrgId(state) {
      return state.currentUser?.idTokenClaims.extension_OrgId;
    },

    currentUserAccountId(state) {
      return state.currentUser?.idTokenClaims.extension_CosmosDbAccountId;
    },

    currentUserIsOwner(state, getters) {
      return getters.currentUserOrgType === 'owner';
    },

    currentUserIsConsumer(state, getters) {
      return getters.currentUserOrgType === 'consumer';
    },

    currentUserIsAdmin(state, getters) {
      return (
        getters.currentUserAccountType === 'OwnerAdmin' ||
        getters.currentUserAccountType === 'ConsumerAdmin'
      );
    },

    currentUserIsDispatcher(state, getters) {
      return getters.currentUserAccountType === 'Dispatcher';
    },

    currentUserIsImageViewer(state, getters) {
      return getters.currentUserAccountType === 'ImageViewer';
    },

    loggedInStatus(state) {
      return state.loggedInStatus;
    },

    passwordChangeState(state) {
      return state.passwordChangeState;
    },
  },

  mutations: {
    setCurrentUser(state, currentUser) {
      if (!currentUser || !currentUser.idToken) {
        throw new Error('trying to store an invalid currentUser');
      }
      state.currentUser = currentUser;
    },

    setLoggedInStatus(state, loggedInStatus) {
      state.loggedInStatus = loggedInStatus;
    },

    setPasswordChangeState(state, passwordChangeState) {
      state.passwordChangeState = passwordChangeState;
      localStorage.setItem('x-ss-passwordChanged', passwordChangeState);
    },

    deletePasswordChangeState(state) {
      state.passwordChangeState = undefined;
      localStorage.removeItem('x-ss-passwordChanged');
    },
  },

  actions: {
    currentUserIsLoggedIn() {
      return Auth.loggedInTest();
    },

    async updateCurrentUser({ dispatch, commit, getters }, operationType = 'read') {
      let user = getters['currentUser'];

      if (!user || !user.idToken || user.idTokenClaims.exp * 1000 <= Date.now()) {
        user = await Auth.getTokenResponse(operationType);
        if (user) {
          commit('setCurrentUser', user);
          dispatch('loginUser');
        }
      }

      return user;
    },

    loginUser({ dispatch }) {
      let intervalTime_sec = parseInt(import.meta.env.VITE_LOGIN_TIMER_INTERVAL_SEC);
      dispatch('loginUserInterval');
      // loginUser is sometimes called multiple times, but we only want one
      // timer interval running at a time. This check makes sure we only ever
      // have one timer kicked off.
      if (loginInterval !== null) {
        clearInterval(loginInterval);
      }
      loginInterval = setInterval(function () {
        dispatch('loginUserInterval');
      }, intervalTime_sec * 1000);
    },

    loginUserInterval({ commit, getters }) {
      if (loggingIn) return;

      if (getters.passwordChangeState === 'changing') {
        // We reloaded (or the tab was restored) mid password-change flow —
        // the heartbeat below never runs while this state is set, so
        // nothing else will ever re-trigger the change-password prompt.
        // Resume it once instead of leaving the app stuck on the loader.
        if (!resumedPasswordChange) {
          resumedPasswordChange = true;
          commit('setLoggedInStatus', 'changingPassword');
          Auth.forcePasswordChange(getters['currentUser']?.account);
        }
        return;
      }

      loggingIn = true;
      let user = getters['currentUser'];
      const loggedInStatus = getters['loggedInStatus'];
      const accountId = getters['currentUserAccountId'];
      const sessionId = localStorage.getItem(sessionIdKey);
      const endpoint = getters.accountsEndpoint;

      if (
        (user && loggedInStatus === 'pending') ||
        loggedInStatus === 'loggedInError' ||
        loggedInStatus === 'loggedIn'
      ) {
        let searchString = '';

        if (sessionId) {
          searchString += `sessionId=${encodeURIComponent(sessionId)}`;
        }

        if (getters.passwordChangeState === 'changed') {
          if (searchString.length > 0) {
            searchString += '&';
          }
          searchString += `passwordChanged=true`;
          commit('deletePasswordChangeState');
        }

        if (searchString.length > 0) {
          searchString = `?${searchString}`;
        }

        const requestUrl = `${endpoint}/${accountId}/login${searchString}`;

        api
          .post(requestUrl)
          .then((response) => {
            // check if this is our first login

            if (response.sessionId) {
              localStorage.setItem(sessionIdKey, response.sessionId);
            }
            if (loggedInStatus !== 'loggedIn') {
              commit('setLoggedInStatus', 'loggedIn');
            }
            loggingIn = false;
            if ('passwordChanged' in response) {
              const changeState = getters.passwordChangeState;
              if (!response.passwordChanged && typeof changeState != 'undefined') {
                // this is our first login
                commit('setPasswordChangeState', 'changing');
                Auth.forcePasswordChange(user?.account);
              }
            }
            // We are logged in and good to go
          })
          .catch((error) => {
            localStorage.removeItem(sessionIdKey);
            console.error('error caught in loginUser in current user', error);
            if (error.status === 409) {
              commit('setLoggedInStatus', 'loggedInFailed');
            } else {
              commit('setLoggedInStatus', 'loggedInError');
            }
            loggingIn = false;
          });
      } else {
        loggingIn = false;
      }
    },

    logoutUser({ commit, getters }) {
      let user = getters['currentUser'];

      if (user === null) {
        return;
      }

      const loggedInStatus = getters['loggedInStatus'];
      const accountId = getters['currentUserAccountId'];
      const sessionId = localStorage.getItem(sessionIdKey);
      const endpoint = getters.accountsEndpoint;
      let requestUrl = `${endpoint}/${accountId}/logout`;

      if (sessionId) {
        requestUrl += `?sessionId=${sessionId}`;
      }

      if (user && loggedInStatus === 'loggedIn') {
        return api
          .post(requestUrl)
          .then(() => {
            commit('setLoggedInStatus', 'pending');
            clearInterval(loginInterval);
            loginInterval = null;
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },

    logoutCurrentUser({ dispatch }) {
      dispatch('logoutUser').then(() => {
        Auth.logout();
      });
    },

    loginCurrentUser() {
      Auth.login();
    },
  },
};
